import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { AppButton } from './AppButton';
import { AppCard } from './AppCard';
import { AppInput } from './AppInput';
import { AppScreen } from './AppScreen';
import { KioskActivationScanner } from './KioskActivationScanner';
import { ScreenHeader } from './ScreenHeader';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { activateKioskWithToken } from '../services/kioskAuthService';
import { kioskAdminErrorMessage } from '../utils/kioskErrors';

type Props = {
  onActivated: () => void;
  onCancel: () => void;
  message?: string | null;
  /** Prefer QR scan first on native; paste form is always available as fallback. */
  preferScan?: boolean;
};

export function KioskActivationPanel({
  onActivated,
  onCancel,
  message,
  preferScan = true,
}: Props) {
  const palette = useAppColors();
  const [token, setToken] = useState('');
  const [scanning, setScanning] = useState(
    preferScan && Platform.OS !== 'web',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const themed = useMemo(
    () => ({
      body: { ...typography.body, color: palette.textSecondary },
      error: { ...typography.body, color: palette.error },
      banner: { ...typography.body, color: palette.warning },
    }),
    [palette],
  );

  const activate = useCallback(
    async (activationToken: string) => {
      const value = activationToken.trim();
      if (!value) {
        setError('Enter or scan an activation token.');
        setScanning(false);
        return;
      }
      setSubmitting(true);
      setError('');
      try {
        await activateKioskWithToken({ activationToken: value });
        onActivated();
      } catch (caught) {
        setError(
          kioskAdminErrorMessage(
            caught,
            'Activation failed. Check the token and try again.',
          ),
        );
        setScanning(false);
      } finally {
        setSubmitting(false);
      }
    },
    [onActivated],
  );

  if (scanning && Platform.OS !== 'web') {
    return (
      <KioskActivationScanner
        onScanned={value => {
          setToken(value);
          activate(value).catch(() => undefined);
        }}
        onCancel={() => setScanning(false)}
      />
    );
  }

  return (
    <AppScreen scroll>
      <ScreenHeader
        title="Activate kiosk"
      />
      <View style={styles.body}>
        {message ? (
          <Text accessibilityRole="alert" style={themed.banner}>
            {message}
          </Text>
        ) : null}
        <AppCard style={styles.card}>
          <Text style={themed.body}>
            This device is not registered yet. Activate it before marking
            attendance.
          </Text>
          <AppInput
            label="Activation token"
            value={token}
            onChangeText={setToken}
            placeholder="Paste activation token"
            autoCapitalize="none"
            autoCorrect={false}
            editable={!submitting}
          />
          {error ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {error}
            </Text>
          ) : null}
        </AppCard>
        <AppButton
          title={submitting ? 'Activating…' : 'Activate'}
          onPress={() => activate(token)}
          disabled={submitting}
        />
        {Platform.OS !== 'web' ? (
          <AppButton
            title="Scan QR code"
            variant="secondary"
            onPress={() => setScanning(true)}
            disabled={submitting}
          />
        ) : null}
        <AppButton
          title="Cancel"
          variant="text"
          onPress={onCancel}
          disabled={submitting}
        />
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.md },
  card: { gap: spacing.md },
});
